import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  ArrowUpRight,
  Filter,
  Search,
  Loader2,
  UploadCloud,
  RefreshCw,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import UploadModal from "../components/UploadModal";
import { listSessionUploads } from "../lib/uploads";

const NA = "not specified in your policy";

function humanSize(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function relativeTime(iso) {
  const d = new Date(iso);
  const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} h ago`;
  const days = Math.floor(diffSec / 86400);
  if (days < 30) return `${days} d ago`;
  return d.toLocaleDateString();
}

const StatusPill = ({ status, stage }) => {
  const label =
    status === "complete"
      ? "Ready"
      : status === "failed"
      ? "Failed"
      : status === "running" && stage === "extracting"
      ? "Extracting"
      : status === "running" && stage === "analysing"
      ? "Analysing"
      : status === "running"
      ? "Working"
      : "Queued";
  const cls =
    status === "complete"
      ? "bg-emerald-50 text-emerald-700"
      : status === "failed"
      ? "bg-red-50 text-red-700"
      : status === "running"
      ? "bg-blue-50 text-blue-700"
      : "bg-slate-100 text-slate-600";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${cls}`}
    >
      {status === "running" && <Loader2 className="h-3 w-3 animate-spin" />}
      {status === "complete" && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />}
      {label}
    </span>
  );
};

export default function MyPolicies() {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const data = await listSessionUploads();
      setItems(data);
    } catch (e) {
      setError(
        e?.response?.data?.detail || "Could not load your policies. Please retry.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const hasWork = items.some(
      (u) => u.analysis_status === "pending" || u.analysis_status === "running",
    );
    if (!hasWork) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [items, load]);

  const filtered = items.filter((p) => {
    const q = query.trim().toLowerCase();
    const matchesQ =
      !q ||
      (p.filename || "").toLowerCase().includes(q) ||
      (p.insurer || "").toLowerCase().includes(q) ||
      (p.vehicle || "").toLowerCase().includes(q);
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "ready" && p.analysis_status === "complete") ||
      (statusFilter === "processing" &&
        (p.analysis_status === "pending" || p.analysis_status === "running"));
    return matchesQ && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar onUploadClick={() => setUploadOpen(true)} />

      <div
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-12"
        data-testid="my-policies-page"
      >
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight text-slate-900">
              My Policies
            </h1>
            <p className="mt-2 text-slate-600">
              Every policy you&apos;ve uploaded from this browser. Only visible
              to you.
            </p>
          </div>
          <div className="flex items-center gap-2 self-start">
            <button
              onClick={load}
              disabled={loading}
              data-testid="policies-refresh"
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 rounded-full px-3 py-2 border border-slate-200 disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={() => setUploadOpen(true)}
              data-testid="policies-add-btn"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-5 py-2.5 font-medium shadow-sm text-sm"
            >
              <Plus className="h-4 w-4" />
              Upload new
            </button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="mt-8 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by filename, insurer, or vehicle…"
              className="w-full bg-white border border-slate-200 rounded-full pl-11 pr-4 py-2.5 text-sm outline-none focus:border-blue-400"
              data-testid="policies-search"
            />
          </div>
          <div
            className="flex items-center gap-1 bg-slate-100 rounded-full p-1"
            data-testid="policies-filter-tabs"
          >
            {[
              { key: "all", label: "All" },
              { key: "ready", label: "Ready" },
              { key: "processing", label: "Processing" },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setStatusFilter(t.key)}
                data-testid={`filter-${t.key}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium ${
                  statusFilter === t.key
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        {loading && items.length === 0 ? (
          <div
            className="mt-8 border border-slate-200 rounded-2xl p-10 flex items-center justify-center text-slate-500"
            data-testid="policies-loading"
          >
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Loading…
          </div>
        ) : error ? (
          <div
            className="mt-8 border border-red-200 rounded-2xl p-5 bg-red-50 text-sm text-red-700"
            data-testid="policies-error"
          >
            {error}
          </div>
        ) : items.length === 0 ? (
          <button
            onClick={() => setUploadOpen(true)}
            data-testid="policies-empty"
            className="mt-8 w-full border-2 border-dashed border-slate-300 hover:border-blue-400 bg-slate-50 hover:bg-blue-50/40 rounded-2xl p-12 flex flex-col items-center justify-center text-center"
          >
            <div className="h-14 w-14 rounded-full bg-white border border-slate-200 flex items-center justify-center text-blue-600 shadow-sm">
              <UploadCloud className="h-6 w-6" />
            </div>
            <div className="mt-4 font-display text-lg font-semibold text-slate-900">
              No policies uploaded yet
            </div>
            <div className="text-sm text-slate-500 mt-1 max-w-md">
              Upload your motor insurance PDF to see it here.
            </div>
          </button>
        ) : (
          <div className="mt-8 space-y-3" data-testid="policies-list">
            {filtered.map((p) => {
              const isReady = p.analysis_status === "complete";
              const insurer =
                isReady && p.insurer && p.insurer !== NA ? p.insurer : null;
              const vehicle =
                isReady && p.vehicle && p.vehicle !== NA ? p.vehicle : null;
              const label = insurer && vehicle ? `${insurer} — ${vehicle}` : p.filename;
              const initials = (insurer || p.filename || "PD")
                .split(/\s+/)
                .map((w) => w[0])
                .slice(0, 2)
                .join("")
                .toUpperCase();
              return (
                <Link
                  key={p.id}
                  to={`/policy/${p.id}`}
                  data-testid={`policy-row-${p.id}`}
                  className="group flex flex-col md:flex-row md:items-center gap-4 md:gap-6 p-5 border border-slate-200 rounded-2xl hover:border-blue-300 hover:shadow-sm bg-white"
                >
                  <div className="flex-1 flex items-start gap-4 min-w-0">
                    <div className="h-12 w-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-display font-semibold shrink-0">
                      {initials}
                    </div>
                    <div className="min-w-0">
                      <div className="font-display font-semibold text-slate-900 truncate">
                        {label}
                      </div>
                      <div className="text-sm text-slate-500 mt-0.5 truncate">
                        {p.filename} · {humanSize(p.size)} · Uploaded{" "}
                        {relativeTime(p.created_at)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 md:gap-6">
                    {isReady && (
                      <div className="text-center">
                        <div className="font-display text-lg font-semibold text-slate-900">
                          {p.clarity_score ?? "—"}
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400">
                          Clarity
                        </div>
                      </div>
                    )}
                    <StatusPill
                      status={p.analysis_status}
                      stage={p.analysis_stage}
                    />
                    <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-blue-600" />
                  </div>
                </Link>
              );
            })}

            {filtered.length === 0 && (
              <div
                className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl"
                data-testid="policies-filter-empty"
              >
                <Filter className="h-6 w-6 text-slate-400 mx-auto" />
                <p className="mt-3 text-slate-600 font-medium">
                  No policies match your filters.
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  Try a different search or clear the filter.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <Footer />
      <UploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={load}
      />
    </div>
  );
}
